import React, { useState, useEffect ,useMemo, useCallback} from "react";
import "../styles/PurchaseOrderSearch.css";
import { apiRequest } from "../utils/api";
import { useNavigate } from "react-router-dom";
import { usePurchaseItems } from "../context/PurchaseItemsContext";

const PurchaseOrderSearch = () => {
  const { setAndFetchPurchaseOrderId } = usePurchaseItems();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [orders, setOrders] = useState([]);
  const pageSize = 8; // 1ページあたりのアイテム数
  const SEARCH_PAGE_SIZE = 15;
  const [viewMode, setViewMode] = useState("all"); // "search" or "all"
  const [statuses, setStatuses] = useState({
    pending: [],
    waiting: [],
    confirmed: []
  });

  const [currentPages, setCurrentPages] = useState({
    pending: 1,
    waiting: 1,
    confirmed: 1,
    all: 1,
    search: 1
  });

  const [totalPages, setTotalPages] = useState({
    pending: 1,
    waiting: 1,
    confirmed: 1,
    all: 1,
    search: 1
  });
  // ステータスをテーブルと一致させる
  // statusMap を useMemo でメモ化
  const statusMap = useMemo(() => ({
    pending: "未入力あり",
    waiting: "査定完了/承諾待ち",
    confirmed: "買取確定/振込待ち",
  }), []);
  // ステータスごとの色分け
  const statusColors = {
    "未入力あり": "#FFD700",
    "査定完了/承諾待ち": "#FFA500",
    "買取確定/振込待ち": "#90EE90",
    "送金手続き完了": "#808080"
  };

  const fetchOrders = useCallback(async (statusKey, newPage = 1) => {
    const apiStatus = statusMap[statusKey]; // API に送るステータスを変換
    if (!apiStatus) {
      console.error(`無効な statusKey: ${statusKey}`);
      return;
    }

    try {
      const response = await apiRequest(`inventory/purchase_orders/?status=${encodeURIComponent(apiStatus)}&page=${newPage}`);

      if (!Array.isArray(response.results)) {
        console.error(`APIレスポンスが不正 (${statusKey}):`, response.results);
        return;
      }

      setStatuses(prev => ({
        ...prev,
        [statusKey]: response.results
      }));

      setTotalPages(prev => ({
        ...prev,
        [statusKey]: Math.ceil(response.count / pageSize)
      }));

      // ページネーションの状態を更新
      setCurrentPages(prev => {
        const updated = { ...prev, [statusKey]: newPage };
        return updated;
      });

    } catch (error) {
      console.error(`買取データの取得に失敗しました (${statusKey})`, error);
    }
  }, [statusMap]);

  // handleSearch を useCallback でラップ
  const handleSearch = useCallback(async (newPage = 1) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await apiRequest(`inventory/purchase_orders/?search=${searchTerm}&page=${newPage}&page_size=15`);

      if (Array.isArray(response.results)) {
        setSearchResults(response.results);
        setViewMode("search"); // 検索モードの指定

        setCurrentPages(prev => {
          const updated = { ...prev, search: newPage };
          return updated;
        });

        setTotalPages(prev => ({
          ...prev,
          search: Math.ceil(response.count / SEARCH_PAGE_SIZE)
        }));

      } else {
        console.error("検索エラー: データが不正", response.results);
      }
    } catch (error) {
      console.error("検索エラー", error);
    }
  }, [searchTerm]); // searchTerm を依存関係に追加

  useEffect(() => {
    
    // 初回ページを 1 にリセット
    setCurrentPages({
      pending: 1,
      waiting: 1,
      confirmed: 1,
      all: 1,
      search: 1
    });

    // 各ステータスのデータ取得（初回は page=1）
    fetchOrders("pending", 1);
    fetchOrders("waiting", 1);
    fetchOrders("confirmed", 1);

    // 全件取得
    fetchAllOrders(1);

    // 検索キーワードがある場合のみ検索を実行
    if (searchTerm.trim()) {
      handleSearch(1);
    }

  }, [fetchOrders, handleSearch, searchTerm]); // 初回のみ実行

    const fetchAllOrders = async (newPage = 1) => {
      try {
        const response = await apiRequest(`inventory/purchase_orders/?page=${newPage}&page_size=15`);

        if (!Array.isArray(response.results)) {
          console.error("APIレスポンスが不正 (全件):", response.results);
          return;
        }
        setViewMode("all");// 検索モードの指定
        setOrders(response.results);
        setTotalPages(prev => ({
          ...prev,
          all: Math.ceil(response.count / SEARCH_PAGE_SIZE)
        }));

      } catch (error) {
        console.error("全件データの取得に失敗しました", error);
      }
    };

  const handlePageChange = (key, newPage) => {
    if (typeof newPage !== "number" || newPage < 1 || newPage > totalPages[key]) {
      console.error(`handlePageChange の newPage が不正: ${newPage}`);
      return;
    }


    setCurrentPages(prev => {
      const updated = { ...prev, [key]: newPage };
      return updated;
    });

    setTimeout(() => {

      if (key === "all") {
        fetchAllOrders(newPage);
      } else if (key === "search") {
        handleSearch(newPage);
      } else {
        fetchOrders(key, newPage);
      }
    }, 0);
  };


  const navigate = useNavigate();
  const onSelectOrder = (id = null) => {
    // `purchaseOrderId` をリセット
    setAndFetchPurchaseOrderId(null); // 明示的に初期化（新規）
    if (id) {
      navigate(`/purchase_orderpage/${id}`);
    } else {
      navigate(`/purchase_orderpage`); // 新規作成用のページ
    }
  };

  return (
    <div className="purchase-order-search-container">
      {/* 左エリア（ステータスごとのリスト + ページネーション） */}
      <div className="purchase-order-search-list">
        <button className="purchase-order-search-new-btn" onClick={() => onSelectOrder(null)}>新規作成</button>
        <p className="purchase-order-no-data">商品到着後、住所不一致が判明した場合　着払い返送or現金書留(手数料1000円)で住所確認</p>
        <div className="purchase-order-search-status-lists">
          {Object.keys(statusMap).map((statusKey, idx) => (
            <div key={idx} className="purchase-order-status">
              <h3>{statusMap[statusKey]}</h3> {/* ステータス名を `statusMap` から取得 */}
              <div className="purchase-order-list">
                {statuses[statusKey]?.length > 0 ? (
                  statuses[statusKey].map(order => (
                    <div key={order.id} className="purchase-order-item" onClick={() => onSelectOrder(order.id)}>
                      <span className="purchase-order-date">{order.date}</span>
                      <span className="purchase-order-number">{order.purchase_number}</span>
                      <span className="purchase-order-name">{order.customer_name}</span>
                      <span className="purchase-order-amount">
                        ￥{Number(order.total_amount).toLocaleString()}
                      </span>
                    </div>
                  ))
                ) : (
                <p className="purchase-order-no-data">データがありません</p>
                )}
              </div>  
              {/* ページネーションコントロール */}
              <div className="pagination-controls">
                <button
                  disabled={currentPages[statusKey] === 1}
                  onClick={() => handlePageChange(statusKey, currentPages[statusKey] - 1)}
                >
                  前のページ
                </button>
                <span>
                  ページ {currentPages[statusKey]} / {totalPages[statusKey]}
                </span>
                <button
                  disabled={currentPages[statusKey] >= totalPages[statusKey]}
                  onClick={() => handlePageChange(statusKey, currentPages[statusKey] + 1)}
                >
                次のページ
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 右エリア（検索 or 全件表示） */}
      <div className="purchase-order-search-box">
        <div className="purchase-order-search-input">
          <input 
            type="text" 
            placeholder="買取番号、名前、商品名などを検索" 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button onClick={() => handleSearch(1)}>検索</button>
        </div>
        <div className="purchase-order-search-results">
          {(searchResults.length > 0 ? searchResults : orders).map(order => (
            <div key={order.id} className="purchase-order-search-item" onClick={() => onSelectOrder(order.id)}>
              <span className="purchase-order-search-status-dot" style={{ backgroundColor: statusColors[order.status] }}></span>
              <span>{order.date}</span> / <b>{order.purchase_number}</b> / {order.customer_name} / ￥{Number(order.total_amount).toLocaleString()}
            </div>
          ))}
        </div>
        {/* ページネーションコントロール */}
        <div className="pagination-controls">
          <button
            disabled={currentPages[viewMode] === 1}
            onClick={() => { handlePageChange(viewMode, currentPages[viewMode] - 1);}}
          >
            前のページ
          </button>
          <span>
            ページ {currentPages[viewMode]} / {totalPages[viewMode]}
           </span>
          <button
            disabled={currentPages[viewMode] >= totalPages[viewMode]}
            onClick={() => { handlePageChange(viewMode, currentPages[viewMode] + 1);}}
          >
            次のページ
          </button>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderSearch;
